import { raceWeekData } from "../raceWeek.js";
import { raceDayData } from "../raceDay.js";

// Simple and reliable timezone conversion - returns time components, not Date object
const getSydneyTimeComponents = () => {
  try {
    // Get current time in Sydney timezone
    const now = new Date();

    // Convert to Sydney time using toLocaleString
    const sydneyTimeString = now.toLocaleString("en-US", {
      timeZone: "Australia/Sydney",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    // Parse the Sydney time string to get individual components
    if (!sydneyTimeString || !sydneyTimeString.includes(",")) {
      throw new Error("Invalid Sydney time string format");
    }

    const [datePart, timePart] = sydneyTimeString.split(", ");
    if (!datePart || !timePart) {
      throw new Error("Could not parse date and time parts");
    }

    const [month, day, year] = datePart.split("/");
    const [hour, minute, second] = timePart.split(":");

    // Validate all components are present
    if (!month || !day || !year || !hour || !minute || !second) {
      throw new Error("Missing time components");
    }

    const timeComponents = {
      year: parseInt(year),
      month: parseInt(month),
      day: parseInt(day),
      hour: parseInt(hour),
      minute: parseInt(minute),
      second: parseInt(second),
    };

    if (process.env.NODE_ENV === "development") {
      console.log("Sydney time string:", sydneyTimeString);
      console.log("Parsed components:", timeComponents);
    }

    return timeComponents;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "Timezone conversion failed, using device time:",
        error.message
      );
    }

    // Fallback: use current device time
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds(),
    };
  }
};

// Cache the timezone conversion result
let cachedSydneyTime = null;
let cacheExpiry = null;
const CACHE_DURATION = 1 * 60 * 1000; // 1 minute cache

const getCachedSydneyTime = () => {
  const now = Date.now();

  // Return cached time if still valid
  if (cachedSydneyTime && cacheExpiry && now < cacheExpiry) {
    return cachedSydneyTime;
  }

  const sydneyTime = getSydneyTimeComponents();
  cachedSydneyTime = sydneyTime;
  cacheExpiry = now + CACHE_DURATION;
  return sydneyTime;
};

// Check if we should use race day data
const shouldUseRaceDayData = () => {
  return false;
  try {
    // Use cached Sydney time components for consistent results
    const sydneyTime = getCachedSydneyTime();

    // Race day starts after August 30th, 2025, 10:00 PM Sydney time
    // Compare time components directly without Date objects
    const raceDayStart = {
      year: 2025,
      month: 8,
      day: 30,
      hour: 22,
      minute: 0,
      second: 0,
    };

    // Log the times being compared in development mode
    if (process.env.NODE_ENV === "development") {
      console.log(
        `🕐 [TIME COMPARISON] Current Sydney time: ${
          sydneyTime.year
        }-${sydneyTime.month.toString().padStart(2, "0")}-${sydneyTime.day
          .toString()
          .padStart(2, "0")} ${sydneyTime.hour
          .toString()
          .padStart(2, "0")}:${sydneyTime.minute
          .toString()
          .padStart(2, "0")}:${sydneyTime.second.toString().padStart(2, "0")}`
      );
      console.log(
        `🎯 [TIME COMPARISON] Race day start: ${
          raceDayStart.year
        }-${raceDayStart.month.toString().padStart(2, "0")}-${raceDayStart.day
          .toString()
          .padStart(2, "0")} ${raceDayStart.hour
          .toString()
          .padStart(2, "0")}:${raceDayStart.minute
          .toString()
          .padStart(2, "0")}:${raceDayStart.second.toString().padStart(2, "0")}`
      );
    }

    // Compare year first, then month, then day, then time
    if (sydneyTime.year < raceDayStart.year) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "📅 [TIME COMPARISON] Year check: false (current < race day)"
        );
      }
      return false;
    }
    if (sydneyTime.year > raceDayStart.year) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "📅 [TIME COMPARISON] Year check: true (current > race day)"
        );
      }
      return true;
    }

    if (sydneyTime.month < raceDayStart.month) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "📅 [TIME COMPARISON] Month check: false (current < race day)"
        );
      }
      return false;
    }
    if (sydneyTime.month > raceDayStart.month) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "📅 [TIME COMPARISON] Month check: true (current > race day)"
        );
      }
      return true;
    }

    if (sydneyTime.day < raceDayStart.day) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "📅 [TIME COMPARISON] Day check: false (current < race day)"
        );
      }
      return false;
    }
    if (sydneyTime.day > raceDayStart.day) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "📅 [TIME COMPARISON] Day check: true (current > race day)"
        );
      }
      return true;
    }

    // Same day, compare time
    if (sydneyTime.hour < raceDayStart.hour) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "🕐 [TIME COMPARISON] Hour check: false (current < race day)"
        );
      }
      return false;
    }
    if (sydneyTime.hour > raceDayStart.hour) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "🕐 [TIME COMPARISON] Hour check: true (current > race day)"
        );
      }
      return true;
    }

    if (sydneyTime.minute < raceDayStart.minute) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "🕐 [TIME COMPARISON] Minute check: false (current < race day)"
        );
      }
      return false;
    }
    if (sydneyTime.minute > raceDayStart.minute) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "🕐 [TIME COMPARISON] Minute check: true (current > race day)"
        );
      }
      return true;
    }

    if (sydneyTime.second < raceDayStart.second) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "🕐 [TIME COMPARISON] Second check: false (current < race day)"
        );
      }
      return false;
    }

    // If we get here, times are equal, so it's race day
    if (process.env.NODE_ENV === "development") {
      console.log(
        "🎯 [TIME COMPARISON] Times are equal - returning true (race day)"
      );
    }
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("Error determining treasure data selection:", error);
    }
    // Fallback to race week data if time verification fails
    return false;
  }
};

// Get the appropriate treasure data based on current time
export const getTreasureData = async () => {
  if (process.env.NODE_ENV === "development") {
    console.log(
      "🔄 [TREASURE DATA] Function called at:",
      new Date().toISOString()
    );
    console.log(
      "🔄 [TREASURE DATA] Call stack:",
      new Error().stack?.split("\n").slice(1, 4).join("\n")
    );
  }

  const useRaceDay = shouldUseRaceDayData();
  const data = useRaceDay ? raceDayData : raceWeekData;

  console.log(
    `📦 [TREASURE DATA] Using ${
      useRaceDay ? "race day" : "race week"
    } data with ${data.length} treasures`
  );
  return data;
};

// Export the check function for use in components
export const checkRaceDayStatus = shouldUseRaceDayData;
